import React, { useContext } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import { DocumentIcon, DownloadIcon } from "@heroicons/react/outline";
import formatFileSize from "lib/formatFileSize";
import Body from "components/Body";
import Button from "components/Button";
import { DialogContext } from "contexts/DialogContext";
import { ACTIONS } from "reducers/dialogReducer";
import useTranslation from "next-translate/useTranslation";
import TopRightShape from "components/Shapes/TopRightShape";
import BottomLeftShape from "components/Shapes/BottomLeftShape";

const DocumentRequest = dynamic(() => import("components/DocumentRequest"), {
  ssr: false,
});

export default function ParagraphBannerFile({ data }) {
  const { t } = useTranslation("common");
  const { dispatch } = useContext(DialogContext);
  const isPrivate = Object.prototype.hasOwnProperty.call(
    data,
    "field_private_file"
  );
  const file = isPrivate ? data.field_private_file : data.field_file;
  const splitted = file.filename.split(".");
  const extension = splitted.pop();
  const filesize = formatFileSize(file.filesize);

  const handleClick = () => {
    if (isPrivate) {
      dispatch({
        type: ACTIONS.OPEN,
        payload: {
          title: "Demande de documentation",
          body: <DocumentRequest file={file} />,
        },
      });
    } else if (typeof window !== "undefined") {
      window.location.assign(
        `/api/file/download?filename=${encodeURIComponent(
          file.filename
        )}&uri=${encodeURIComponent(file.uri.value)}`
      );
    }
  };

  return (
    <div className="w-full mx-auto lg:max-w-screen-lg">
      <div className="relative bg-primary p-16 rounded-lg text-white">
        <div className="absolute top-0 right-0" aria-hidden="true">
          <TopRightShape className="w-36 h-auto text-white/30" />
        </div>
        <div className="absolute bottom-0 left-0" aria-hidden="true">
          <BottomLeftShape className="w-36 h-auto text-white/30" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div
              className="inline-block p-4 bg-primary-dark rounded-md mb-4"
              aria-hidden="true"
            >
              <DocumentIcon className="w-8 h-8" />
            </div>

            <h2 className="text-2xl font-important mb-1">{data.field_title}</h2>

            <div className="flex items-center gap-2 text-sm uppercase mb-4">
              <span>{extension}</span>
              <span>-</span>
              <span>{filesize}</span>
            </div>

            {data.field_text?.processed && (
              <div className="prose max-w-none text-white mb-12">
                <Body value={data.field_text.processed} />
              </div>
            )}

            <div>
              <Button
                as="a"
                theme="primary-dark"
                onClick={() => handleClick()}
                inverse
              >
                <span className="flex items-center gap-2">
                  <DownloadIcon className="h-5 w-5 text-primary group-hover:text-white transition-colors" />
                  <span className="font-important font-medium">
                    {t("utils.files.download")}
                  </span>
                </span>
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-center">
            <div className="relative w-4/5">
              <div className="aspect-w-1 aspect-h-1 rounded-full bg-primary-dark" />
              <div className="absolute inset-0 -rotate-12 flex items-center justify-center">
                <div className="w-2/3">
                  <div className="aspect-a4 rounded-md overflow-hidden">
                    <Image
                      src={data.field_image.image_style_uri.a4}
                      alt={data.field_image.resourceIdObjMeta.alt}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      quality={100}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
